/**
 * Reset-password screen — enter the emailed 6-digit code + set a new password.
 *
 * PORTED from apps/cryb-native/app/(auth)/reset-password.tsx (P3 AUTH group).
 *
 * P1-AUTH REDESIGN: gradient/blobs + a single "paste the long token" field are
 * replaced by the design system (ParticleBackground + GlassSurface + LogoWordmark)
 * and a clean 6-box OTP input. The flow is email + 6-digit code + new password,
 * verified server-side by POST /auth/reset-password (resetPasswordWithCode). The
 * email is normally carried in from ForgotPassword via route params; if it's
 * missing (e.g. a legacy deep link) the user can type it here.
 */
import { useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import LogoWordmark from '../components/LogoWordmark';
import Asset3D from '../components/ui/Asset3D';
import { GlassSurface, GlassButton } from '../components/glass';
import { resetPasswordWithCode, requestPasswordReset, PASSWORD_RULES } from '../services/api';
import type { AuthStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../constants/theme';

type Nav = NativeStackNavigationProp<AuthStackParamList, 'ResetPassword'>;
type Rt = RouteProp<AuthStackParamList, 'ResetPassword'>;

const OTP_LENGTH = 6;

// ── 6-box OTP input ──────────────────────────────────────────────────────────
// One hidden TextInput backs six visible boxes: keeps native paste/autofill of a
// one-time code working (the OS delivers the whole string to one field) while the
// boxes render one glyph each. Tapping any box focuses the field at the caret.
function OtpInput({
  value,
  onChange,
  hasError,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  onSubmit?: () => void;
}) {
  const ref = useRef<TextInput>(null);
  const [focused, setFocused] = useState(false);
  const cells = useMemo(() => Array.from({ length: OTP_LENGTH }), []);

  const handleChange = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '').slice(0, OTP_LENGTH);
    onChange(digits);
    if (digits.length === OTP_LENGTH) onSubmit?.();
  };

  return (
    <Pressable style={styles.otpRow} onPress={() => ref.current?.focus()} accessibilityLabel="6-digit verification code">
      {cells.map((_, i) => {
        const filled = i < value.length;
        const isCursor = focused && i === value.length;
        return (
          <View
            key={i}
            style={[
              styles.otpCell,
              (isCursor || filled) && styles.otpCellActive,
              hasError && styles.otpCellError,
            ]}
          >
            <Text style={styles.otpDigit}>{value[i] ?? ''}</Text>
          </View>
        );
      })}
      <TextInput
        ref={ref}
        style={styles.otpHiddenInput}
        value={value}
        onChangeText={handleChange}
        keyboardType="number-pad"
        textContentType="oneTimeCode"
        autoComplete="sms-otp"
        maxLength={OTP_LENGTH}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        caretHidden
        autoFocus
      />
    </Pressable>
  );
}

export default function ResetPasswordScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const cardWidth = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 460 : 420, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  const paramEmail = route.params?.email;
  const [email, setEmail] = useState(paramEmail ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [done, setDone] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [passwordTouched, setPasswordTouched] = useState(false);

  const ruleState = useMemo(
    () => PASSWORD_RULES.rules.map((r) => ({ ...r, ok: r.test(password) })),
    [password],
  );
  const allRulesPass = ruleState.every((r) => r.ok);

  async function handleReset() {
    setFieldErrors({});
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = 'Enter your email.';
    if (code.length !== OTP_LENGTH) errs.code = 'Enter the 6-digit code.';
    if (!allRulesPass) { errs.password = 'Password does not meet all requirements.'; setPasswordTouched(true); }
    if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }

    setLoading(true);
    const result = await resetPasswordWithCode(email.trim(), code, password, confirmPassword);
    setLoading(false);

    if (result.success) {
      setDone(true);
      return;
    }

    const details = (result as any).details as Record<string, any> | undefined;
    const fe = details?.fieldErrors;
    const next: Record<string, string> = {};
    if (fe && typeof fe === 'object') {
      if (typeof fe.code === 'string') next.code = fe.code;
      if (typeof fe.confirmPassword === 'string') next.confirmPassword = fe.confirmPassword;
      if (fe.password && typeof fe.password === 'object') {
        next.password = Object.values(fe.password as Record<string, string>).join('. ');
      } else if (typeof fe.password === 'string') {
        next.password = fe.password;
      }
    }
    if (Object.keys(next).length > 0) {
      setFieldErrors(next);
    } else {
      Alert.alert('Reset failed', result.error || 'Invalid or expired code. Request a new one.');
    }
  }

  async function handleResend() {
    if (!email.trim()) {
      setFieldErrors((e) => ({ ...e, email: 'Enter your email to resend.' }));
      return;
    }
    setResending(true);
    const result = await requestPasswordReset(email.trim());
    setResending(false);
    if (result.success) {
      Alert.alert('Code sent', 'We sent a new 6-digit code to your email.');
    } else {
      const details = (result as any).details as Record<string, any> | undefined;
      const retryAfter = typeof details?.retryAfter === 'number' ? details.retryAfter : undefined;
      Alert.alert(
        'Please wait',
        retryAfter != null
          ? `Too many attempts. Try again in ${retryAfter < 60 ? `${retryAfter} seconds` : `${Math.ceil(retryAfter / 60)} minutes`}.`
          : (result.error || 'Could not resend the code.'),
      );
    }
  }

  return (
    <View style={styles.root}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.center,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.xxl },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.stack, cardWidth]}>
            <View style={styles.brand}>
              <LogoWordmark size={34} />
            </View>

            {done ? (
              <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
                <Asset3D name="success" size={104} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.title}>Password reset</Text>
                <Text style={styles.subtitle}>
                  Your password has been updated. Sign in with your new password.
                </Text>
                <GlassButton
                  title="Sign In"
                  onPress={() => navigation.navigate('Login')}
                  fullWidth
                  size="lg"
                />
              </GlassSurface>
            ) : (
              <GlassSurface variant="card" radius={radius.xl} contentStyle={styles.cardBody} style={styles.card}>
                <Text style={styles.title}>Enter your code</Text>
                <Text style={styles.subtitle}>
                  Enter the 6-digit code we emailed you, then choose a new password.
                </Text>

                {/* Email — prefilled from ForgotPassword; editable for a deep link. */}
                {!paramEmail && (
                  <>
                    <TextInput
                      style={[styles.input, fieldErrors.email && styles.inputError]}
                      placeholder="your@email.com"
                      placeholderTextColor={colors.muted}
                      value={email}
                      onChangeText={(t) => { setEmail(t); if (fieldErrors.email) setFieldErrors((e) => ({ ...e, email: '' })); }}
                      autoCapitalize="none"
                      keyboardType="email-address"
                      autoComplete="email"
                      returnKeyType="next"
                    />
                    {!!fieldErrors.email && <Text style={styles.fieldError}>{fieldErrors.email}</Text>}
                  </>
                )}

                {/* 6-box OTP */}
                <OtpInput
                  value={code}
                  onChange={(v) => { setCode(v); if (fieldErrors.code) setFieldErrors((e) => ({ ...e, code: '' })); }}
                  hasError={!!fieldErrors.code}
                />
                {!!fieldErrors.code && <Text style={[styles.fieldError, styles.otpError]}>{fieldErrors.code}</Text>}

                <TouchableOpacity
                  onPress={handleResend}
                  disabled={resending}
                  activeOpacity={0.6}
                  style={styles.resendRow}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  accessibilityRole="button"
                  accessibilityLabel="Resend code"
                >
                  <Text style={styles.resendText}>{resending ? 'Sending…' : 'Resend code'}</Text>
                </TouchableOpacity>

                {/* New password */}
                <View style={styles.passwordWrap}>
                  <TextInput
                    style={[styles.passwordInput, fieldErrors.password && styles.inputError]}
                    placeholder="New password"
                    placeholderTextColor={colors.muted}
                    value={password}
                    onChangeText={(t) => { setPassword(t); setPasswordTouched(true); if (fieldErrors.password) setFieldErrors((e) => ({ ...e, password: '' })); }}
                    onFocus={() => setPasswordTouched(true)}
                    secureTextEntry={!showPass}
                    textContentType="newPassword"
                    autoComplete="password-new"
                    returnKeyType="next"
                  />
                  <Pressable
                    onPress={() => setShowPass((p) => !p)}
                    style={styles.eyeBtn}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    accessibilityRole="button"
                    accessibilityLabel={showPass ? 'Hide password' : 'Show password'}
                  >
                    <Ionicons name={showPass ? 'eye-off-outline' : 'eye-outline'} size={20} color={colors.muted} />
                  </Pressable>
                </View>
                {!!fieldErrors.password && <Text style={styles.fieldError}>{fieldErrors.password}</Text>}

                {passwordTouched && (
                  <View style={styles.rulesList}>
                    {ruleState.map((r) => (
                      <View key={r.key} style={styles.ruleRow}>
                        <Ionicons
                          name={r.ok ? 'checkmark-circle' : 'ellipse-outline'}
                          size={15}
                          color={r.ok ? colors.success : colors.faint}
                        />
                        <Text style={[styles.ruleText, r.ok && styles.ruleTextOk]}>{r.label}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Confirm */}
                <TextInput
                  style={[styles.input, fieldErrors.confirmPassword && styles.inputError]}
                  placeholder="Confirm new password"
                  placeholderTextColor={colors.muted}
                  value={confirmPassword}
                  onChangeText={(t) => { setConfirmPassword(t); if (fieldErrors.confirmPassword) setFieldErrors((e) => ({ ...e, confirmPassword: '' })); }}
                  secureTextEntry={!showPass}
                  textContentType="newPassword"
                  autoComplete="password-new"
                  returnKeyType="done"
                  onSubmitEditing={handleReset}
                />
                {!!fieldErrors.confirmPassword && <Text style={styles.fieldError}>{fieldErrors.confirmPassword}</Text>}

                <GlassButton
                  title="Reset Password"
                  onPress={handleReset}
                  loading={loading}
                  disabled={loading}
                  fullWidth
                  size="lg"
                  style={styles.submit}
                />
              </GlassSurface>
            )}

            <TouchableOpacity
              onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Login'))}
              style={styles.bottomRow}
              activeOpacity={0.6}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Go back"
            >
              <Text style={styles.bottomLink}>← Back</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  center: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xxl,
  },
  stack: { width: '100%' },
  brand: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  card: { marginBottom: spacing.xl },
  cardBody: { padding: spacing.xxl },
  iconBadge: {
    width: 56,
    height: 56,
    borderRadius: radius.full,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    ...typographyScale.h2,
    textAlign: 'center',
    color: colors.ink,
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typographyScale.body,
    textAlign: 'center',
    color: colors.ink2,
    marginBottom: spacing.xl,
  },
  // OTP
  otpRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'relative',
  },
  otpCell: {
    flex: 1,
    height: 54,
    marginHorizontal: spacing.xs,
    borderRadius: radius.md,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  otpCellActive: {
    borderColor: colors.primary,
  },
  otpCellError: {
    borderColor: colors.error,
  },
  otpDigit: {
    ...typographyScale.h2,
    color: colors.ink,
  },
  otpHiddenInput: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    opacity: 0,
  },
  otpError: {
    textAlign: 'center',
    marginLeft: 0,
  },
  resendRow: {
    alignSelf: 'center',
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  resendText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  passwordWrap: {
    position: 'relative',
  },
  passwordInput: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    paddingRight: 44,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  eyeBtn: {
    position: 'absolute',
    right: spacing.md,
    top: 14,
  },
  input: {
    height: 48,
    backgroundColor: colors.white,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    ...typographyScale.body,
    color: colors.ink,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    marginBottom: spacing.md,
  },
  inputError: {
    borderColor: colors.error,
  },
  fieldError: {
    ...typographyScale.caption,
    color: colors.error,
    marginTop: -spacing.sm,
    marginBottom: spacing.md,
    marginLeft: spacing.xs,
  },
  rulesList: {
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
    gap: spacing.xs,
  },
  ruleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  ruleText: {
    ...typographyScale.caption,
    color: colors.muted,
  },
  ruleTextOk: {
    color: colors.ink2,
    ...fonts.medium,
  },
  submit: {
    marginTop: spacing.sm,
  },
  bottomRow: {
    alignSelf: 'center',
  },
  bottomLink: {
    ...typographyScale.body,
    color: colors.primary,
    ...fonts.semibold,
  },
});
